// src/app/shared/components/search-bar/search-bar.component.ts
import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  Output,
  SimpleChanges,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Subject, Subscription } from 'rxjs';
import { debounceTime, distinctUntilChanged, map } from 'rxjs/operators';

@Component({
  selector: 'app-search-bar',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './search-bar.component.html',
  styleUrls: ['./search-bar.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SearchBarComponent implements OnChanges, OnDestroy {
  @Input() placeholder = 'Search';
  @Input() debounceMs = 1000;
  @Input() value = '';
  @Input() disabled = false;

  @Output() searchChange = new EventEmitter<string>();

  internalValue = '';

  private readonly input$ = new Subject<string>();
  private inputSub?: Subscription;

  constructor() {
    this.bindSearch();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['value']) {
      this.internalValue = String(this.value ?? '');
    }

    if (changes['debounceMs'] && !changes['debounceMs'].firstChange) {
      this.bindSearch();
    }
  }

  ngOnDestroy(): void {
    this.inputSub?.unsubscribe();
    this.input$.complete();
  }

  onInputChange(value: string | null): void {
    const normalized = String(value ?? '').trimStart();
    this.internalValue = normalized;
    this.input$.next(normalized);
  }

  clear(): void {
    this.internalValue = '';
    this.searchChange.emit('');
  }

  private bindSearch(): void {
    this.inputSub?.unsubscribe();

    this.inputSub = this.input$
      .pipe(
        map((value) => String(value ?? '').trimStart()),
        debounceTime(this.debounceMs),
        distinctUntilChanged(),
      )
      .subscribe((value) => {
        this.searchChange.emit(value);
      });
  }
}